/**
 * WHO TEXTED (ops #25), AND WHAT THE OFFICE DOES ABOUT IT (ops #26, part 1). Every text to the
 * business line that nobody has handled yet, newest first, and the actions on each.
 *
 * `message.received` lands each text on the household or the enquiry it belongs to and raises a
 * follow-up and a notice beside it. The notice is how a text is seen this afternoon; this list is
 * how it is seen at all once the notice has been read, and it is the one place the words themselves
 * are shown.
 *
 * IT IS THE THIRD TAB OF THE FOLLOW-UPS SCREEN, "Texts", beside the calls, and not a word of its own
 * in the rail. "Who texted" is the calls question asked of the other way in, and the rail marks
 * Tasks for all three tabs.
 *
 * THE BODY IS DRAWN AS TEXT, AND NEVER AS A PAGE. A text is typed by anybody who has the number, so
 * its body is the one string in this product a stranger wrote. It sits in a paragraph as a child,
 * where React escapes it, so a body of markup shows as the characters somebody typed and not one
 * element of it reaches the page. Nothing here reads it, trims it or cuts it short: a long text
 * wraps at the phone's width, a line she broke stays broken, and a word with no spaces in it breaks
 * anywhere rather than pushing the card off the screen. `Messages.test.tsx` fails the day it goes
 * through `innerHTML` instead.
 *
 * THE PICTURES STAY WITH THE PHONE SYSTEM, the way a call's recording does, so a row says how many
 * came and nothing more. Where the phone system's scrub took something out, the row names the kind
 * and never the value. The row links to the phone system's own page for the text, behind its own
 * sign-in, where the pictures are (faithfully-kept-phone#17), wherever the server names one. Those
 * parts of a text are drawn the same way in a household's or an enquiry's thread, and live with it
 * (`TextThread.tsx`, ops #26, part 2).
 *
 * THE ACTIONS, under each text, every one the server's to allow and to record:
 *
 *   Call            a `tel:` link to the number it came from, so the handset places the call and
 *                   ops places none. Where a mark holds calls to her the number is written out
 *                   instead, never offered (`CallLink`).
 *   Handled, Spam   asked once more, because a text is marked once and never put back. Spam is
 *                   spam and nothing else: no stop and no do-not-contact mark comes of it.
 *   Whose is it?    for a text that landed on nobody, the people and the open enquiries whose
 *                   number it is, and nothing chosen for her: a guess is what landing on nobody
 *                   was there to stop. It goes on one record, once.
 *   Open an issue   for a text on a household, on one of its last visits, found by her unless the
 *                   office says not.
 *   Reply           for a text from a person on a household, whose number the forget has not
 *                   blanked (ops #32): the composer opens under the card for her, and a reply written
 *                   marks the text handled, so the list is read again (`Composer.tsx`).
 *
 * A refusal is shown in the server's own words, never its code. A person who texted first, on an
 * enquiry or on nobody, is answered by a call or an email (the owner, 2026-09-23), and only a booked
 * client is answered here (the owner, ops #32, 2026-10-16); the foot says so rather than leaving
 * somebody hunting for a Reply.
 *
 * AND ABOVE THE LIST, THE STOPS WAITING FOR A DECISION (ops #33): a text whose words may ask us to stop
 * texting is held for the office (ops #30), refusing every text to her number until somebody decides.
 * Each open hold is a card in a band of its own, oldest said first, with her words, the clock and the
 * two decisions (`StopHoldCard`), and her text below carries a gold pill saying a stop may be inside
 * it. Nothing is drawn when none waits. Clear is offered to an owner or a manager, and the server
 * decides either way.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ISSUE_KINDS, VISIT_STATUS_LABELS, mayLiftMarks, type IssueKind } from '@fk/shared';
import {
  ApiError,
  attachMessage,
  closeMessage,
  listMessages,
  listMessageVisits,
  listStopHolds,
  openIssueFromMessage,
  type AttachChoice,
  type Me,
  type MessageList,
  type MessageRow,
  type MessageVisit,
  type StopHoldList,
  type StopHoldRow,
} from '../api.ts';
import { Card, MEASURE, Pill, ScreenTitle } from '../components/AppShell.tsx';
import { CallLink } from '../components/CallLink.tsx';
import { Button, SelectField, TextAreaField, TextField } from '../components/Field.tsx';
import { Busy, EmptyNotice, ErrorNotice, SavedNotice } from '../components/Notice.tsx';
import { StopHoldCard } from '../components/StopHold.tsx';
import { CheckField } from '../components/VisitBits.tsx';
import { formatInstant, formatPhone, shortDay } from '../format.ts';
import { MAY_BE_INSIDE_WORDS } from '../holds.ts';
import {
  INBOUND_KEYWORD_LABELS,
  INBOUND_MESSAGE_STATE_LABELS,
  ISSUE_KIND_LABELS,
} from '../labels.ts';
import { OfficeTabs } from './Tasks.tsx';
import { TextComposer } from './Composer.tsx';
import { TextBody, isPhonePage, photoWords, removedWords } from './TextThread.tsx';

/**
 * Who sent it, in the words the office would use: the person whose number it is, then the enquiry
 * it landed on, then the household, then the number itself. A number on two households lands on
 * neither, so it is shown as the number rather than as a guess.
 */
function senderWords(row: MessageRow): string {
  return (
    row.contactName ??
    row.enquiryName ??
    row.clientName ??
    formatPhone(row.fromE164) ??
    'A sender with no number on file'
  );
}

/** One choice of whose a text is, as the office reads it. */
function choiceWords(choice: AttachChoice): string {
  return choice.kind === 'contact'
    ? `${choice.contactName}, ${choice.clientName}`
    : `Enquiry: ${choice.enquiryName}`;
}

/** Whether a text is on anybody yet. */
const landed = (row: MessageRow) =>
  row.clientId !== null || row.contactId !== null || row.enquiryId !== null;

/**
 * Who a held text is from, by the text's own names, then its number while the text still carries one,
 * and never the number the hold keeps: a forget blanks the text's and leaves the hold's.
 */
function holdSender(row: StopHoldRow): string {
  return (
    row.contactName ??
    row.enquiryName ??
    row.clientName ??
    formatPhone(row.text.fromE164) ??
    'A number since forgotten'
  );
}

/** What the office said it did, shown above the list once the list has been read again. */
type Done = (sentence: string) => void;

export function Messages({ me }: { me: Me }) {
  const [list, setList] = useState<MessageList | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [holds, setHolds] = useState<StopHoldList | null>(null);
  const [holdsError, setHoldsError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [said, setSaid] = useState<string | null>(null);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  const done = useCallback<Done>(
    (sentence) => {
      setSaid(sentence);
      reload();
    },
    [reload],
  );

  useEffect(() => {
    let live = true;
    setError(null);
    setList(null);
    setHoldsError(null);
    listMessages()
      .then((answer) => {
        if (live) setList(answer);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError ? thrown : new ApiError(0, 'error', 'The texts did not load.'),
        );
      });
    // The holds on their own read, so the texts still show when this one does not answer.
    listStopHolds()
      .then((answer) => {
        if (live) setHolds(answer);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setHoldsError(
          thrown instanceof ApiError ? thrown.message : 'The stops waiting did not load.',
        );
      });
    return () => {
      live = false;
    };
  }, [attempt]);

  const rows = list?.messages ?? [];
  const waiting = holds?.holds ?? [];
  const canClear = mayLiftMarks(me.role);

  return (
    <div>
      <ScreenTitle title="Texts" subtitle="Who texted the business line, and what they wrote." />
      <OfficeTabs here="texts" />

      {said ? (
        <div className="mb-3 md:max-w-3xl">
          <SavedNotice>{said}</SavedNotice>
        </div>
      ) : null}

      {holdsError ? (
        <div className="mb-4 md:max-w-3xl">
          <ErrorNotice
            title="The stops waiting for a decision did not load"
            message={holdsError}
            onRetry={reload}
          />
        </div>
      ) : waiting.length > 0 ? (
        <section aria-labelledby="holds-waiting" className="mb-5 md:max-w-3xl">
          <h2 id="holds-waiting" className="font-serif text-lg">
            Stops waiting for a decision
          </h2>
          <ol aria-label="Stops waiting for a decision" className="mt-2 flex flex-col gap-3">
            {waiting.map((row) => (
              <li key={row.hold.id}>
                <StopHoldCard
                  hold={row.hold}
                  sender={holdSender(row)}
                  body={row.text.body}
                  canClear={canClear}
                  onDone={done}
                >
                  <HeldLinks row={row} />
                </StopHoldCard>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      {error ? (
        <ErrorNotice title="The texts did not load" message={error.message} onRetry={reload} />
      ) : list === null ? (
        <Busy what="the texts" />
      ) : (
        <>
          {rows.length === 0 ? (
            <EmptyNotice title="No texts waiting">
              <p>
                A text to the business line shows here, on the household or the enquiry it belongs
                to, until somebody handles it.
              </p>
            </EmptyNotice>
          ) : (
            <ol aria-label="Texts" className="flex flex-col gap-3 md:max-w-3xl">
              {rows.map((row) => (
                <li key={row.id}>
                  <TextCard row={row} onDone={done} />
                </li>
              ))}
            </ol>
          )}
          {rows.length >= list.limit ? (
            <p className={`mt-3 text-xs text-navy/60 ${MEASURE}`}>
              The newest {list.limit} are shown. The older ones are still kept.
            </p>
          ) : null}
          <p className={`mt-4 text-xs text-navy/60 ${MEASURE}`}>
            Handled and spam take a text off this list for good. Somebody who texted first is
            answered by a call or an email; a booked client is answered here.
          </p>
        </>
      )}
    </div>
  );
}

/** One text. Newest first, and every card the same shape at every width. */
function TextCard({ row, onDone }: { row: MessageRow; onDone: Done }) {
  const sender = senderWords(row);
  const number = formatPhone(row.fromE164);
  return (
    <Card>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <h3 className="min-w-0 font-serif text-lg leading-tight wrap-anywhere">{sender}</h3>
        <p className="text-xs text-navy/65">
          <time dateTime={row.receivedAt}>{formatInstant(row.receivedAt)}</time>
        </p>
      </div>
      {number && number !== sender ? <p className="text-sm text-navy/75">{number}</p> : null}

      <TextBody body={row.body} />

      {row.redacted.length > 0 ? (
        <p className="mt-1 text-xs text-navy/65">
          Taken out before it reached us: {removedWords(row.redacted)}.
        </p>
      ) : null}

      <div className="mt-2 flex flex-wrap gap-2">
        {row.keyword ? <Pill tone="gold">{INBOUND_KEYWORD_LABELS[row.keyword]}</Pill> : null}
        {row.holdId ? <Pill tone="gold">{MAY_BE_INSIDE_WORDS}</Pill> : null}
        {row.mediaCount > 0 ? <Pill>{photoWords(row.mediaCount)}</Pill> : null}
        <Pill>{INBOUND_MESSAGE_STATE_LABELS[row.handled]}</Pill>
      </div>

      <ul className="mt-2 flex flex-col text-sm">
        {row.clientId ? (
          <li>
            <Link
              to={`/clients/${row.clientId}`}
              className="flex min-h-11 desk:min-h-9 items-center underline underline-offset-2"
            >
              <span className="wrap-anywhere">{row.clientName ?? 'Their household'}</span>
            </Link>
          </li>
        ) : null}
        {row.enquiryId ? (
          <li>
            <Link
              to="/enquiries"
              className="flex min-h-11 desk:min-h-9 items-center underline underline-offset-2"
            >
              <span className="wrap-anywhere">Enquiry: {row.enquiryName ?? 'no name on it'}</span>
            </Link>
          </li>
        ) : null}
        {row.taskId ? (
          <li>
            <Link
              to="/tasks"
              className="flex min-h-11 desk:min-h-9 items-center underline underline-offset-2"
            >
              The follow-up to answer it
            </Link>
          </li>
        ) : null}
        {isPhonePage(row.phonePage) ? (
          <li>
            <a
              href={row.phonePage}
              target="_blank"
              rel="noreferrer"
              className="flex min-h-11 desk:min-h-9 items-center underline underline-offset-2"
            >
              See it in the phone system
            </a>
          </li>
        ) : null}
      </ul>

      <TextActions row={row} onDone={onDone} />
    </Card>
  );
}

/** Where a held text belongs, as the text's own card links them: her household, her enquiry, the phone system. */
function HeldLinks({ row }: { row: StopHoldRow }) {
  const links = [
    row.clientId ? (
      <li key="household">
        <Link
          to={`/clients/${row.clientId}`}
          className="flex min-h-11 desk:min-h-9 items-center underline underline-offset-2"
        >
          <span className="wrap-anywhere">{row.clientName ?? 'Their household'}</span>
        </Link>
      </li>
    ) : null,
    row.enquiryId ? (
      <li key="enquiry">
        <Link
          to="/enquiries"
          className="flex min-h-11 desk:min-h-9 items-center underline underline-offset-2"
        >
          <span className="wrap-anywhere">Enquiry: {row.enquiryName ?? 'no name on it'}</span>
        </Link>
      </li>
    ) : null,
    isPhonePage(row.phonePage) ? (
      <li key="phone">
        <a
          href={row.phonePage}
          target="_blank"
          rel="noreferrer"
          className="flex min-h-11 desk:min-h-9 items-center underline underline-offset-2"
        >
          See it in the phone system
        </a>
      </li>
    ) : null,
  ].filter((link) => link !== null);
  return links.length === 0 ? null : <ul className="mt-2 flex flex-col text-sm">{links}</ul>;
}

type Opened = 'handled' | 'spam' | 'attach' | 'issue' | 'reply';

/**
 * Whether the office may answer a text from here: one from a person on a household, whose number the
 * forget has not blanked. Somebody on an enquiry or on nobody texted first, and is rung or emailed.
 */
const answerable = (row: MessageRow) =>
  row.contactId !== null && row.clientId !== null && row.fromE164 !== null;

/**
 * THE ACTIONS ON ONE TEXT. One of them open at a time, under the card it belongs to, because a
 * sheet over the list hides the text the action is about. On a refusal the server's sentence is
 * shown and nothing typed is touched.
 */
function TextActions({ row, onDone }: { row: MessageRow; onDone: Done }) {
  const [opened, setOpened] = useState<Opened | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const toggle = (which: Opened) => {
    setProblem(null);
    setOpened((current) => (current === which ? null : which));
  };

  async function run(work: () => Promise<unknown>, sentence: string) {
    setBusy(true);
    setProblem(null);
    try {
      await work();
      onDone(sentence);
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not recorded.');
    } finally {
      setBusy(false);
    }
  }

  const close = (as: 'handled' | 'spam') =>
    void run(() => closeMessage(row.id, as), as === 'handled' ? 'Marked handled.' : 'Marked spam.');

  return (
    <div className="mt-3 border-t border-navy/10 pt-3">
      {problem ? (
        <div className="mb-3">
          <ErrorNotice title="Not recorded" message={problem} />
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {row.fromE164 ? <CallLink phoneE164={row.fromE164} marked={row.doNotContact.call} /> : null}
        {answerable(row) ? (
          <Button tone="quiet" onClick={() => toggle('reply')} disabled={busy}>
            Reply
          </Button>
        ) : null}
        <Button tone="quiet" onClick={() => toggle('handled')} disabled={busy}>
          Handled
        </Button>
        <Button tone="quiet" onClick={() => toggle('spam')} disabled={busy}>
          Spam
        </Button>
        {!landed(row) && row.attachTo.length > 0 ? (
          <Button tone="quiet" onClick={() => toggle('attach')} disabled={busy}>
            Whose is it?
          </Button>
        ) : null}
        {row.clientId ? (
          <Button tone="quiet" onClick={() => toggle('issue')} disabled={busy}>
            Open an issue
          </Button>
        ) : null}
      </div>

      {opened === 'handled' || opened === 'spam' ? (
        <AskOnce as={opened} busy={busy} onYes={() => close(opened)} onNo={() => setOpened(null)} />
      ) : null}
      {opened === 'reply' && row.contactId !== null && answerable(row) ? (
        <TextComposer
          people={[{ id: row.contactId, displayName: senderWords(row) }]}
          answering={{ id: row.id, contactId: row.contactId, body: row.body }}
          // Her words are on the card right above the box.
          quote={false}
          // A stop waiting for a decision is in the band at the top of this list.
          onTextsList
          onSent={onDone}
          onCancel={() => setOpened(null)}
        />
      ) : null}
      {opened === 'attach' ? (
        <WhoseIsIt
          row={row}
          busy={busy}
          onRefused={setProblem}
          onPut={(choice) =>
            void run(
              () =>
                attachMessage(
                  row.id,
                  choice.kind === 'contact'
                    ? { contactId: choice.contactId }
                    : { enquiryId: choice.enquiryId },
                ),
              'Put on their record.',
            )
          }
          onCancel={() => setOpened(null)}
        />
      ) : null}
      {opened === 'issue' ? (
        <IssueFromText
          row={row}
          busy={busy}
          onRefused={setProblem}
          onOpen={(issue) =>
            void run(
              () => openIssueFromMessage(row.id, issue),
              'Issue opened. It is on the Issues screen, with this text named in it.',
            )
          }
          onCancel={() => setOpened(null)}
        />
      ) : null}
    </div>
  );
}

/** Handled or spam, asked once more: a text is marked once, and nothing puts it back. */
function AskOnce({
  as,
  busy,
  onYes,
  onNo,
}: {
  as: 'handled' | 'spam';
  busy: boolean;
  onYes: () => void;
  onNo: () => void;
}) {
  return (
    <div className="mt-3 rounded-lg border border-navy/15 bg-parchment p-3">
      <p className={`text-sm ${MEASURE}`}>
        {as === 'handled'
          ? 'Mark this text handled? It leaves this list with your name on it, and a text marked once cannot be put back.'
          : 'Mark this text spam? It leaves this list with your name on it, and cannot be put back. Nothing else changes: their number is not stopped, and no do-not-contact mark is set.'}
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button onClick={onYes} disabled={busy}>
          {busy ? 'Recording' : as === 'handled' ? 'Mark it handled' : 'Mark it spam'}
        </Button>
        <Button tone="quiet" onClick={onNo} disabled={busy}>
          Not yet
        </Button>
      </div>
    </div>
  );
}

/**
 * WHOSE A TEXT ON NOBODY IS: the people and the open enquiries the server found with the number it
 * came from, and none of them chosen until somebody chooses.
 */
function WhoseIsIt({
  row,
  busy,
  onRefused,
  onPut,
  onCancel,
}: {
  row: MessageRow;
  busy: boolean;
  onRefused: (sentence: string) => void;
  onPut: (choice: AttachChoice) => void;
  onCancel: () => void;
}) {
  const [chosen, setChosen] = useState<number | null>(null);

  function put() {
    const choice = chosen === null ? undefined : row.attachTo[chosen];
    if (!choice) {
      onRefused('Choose whose text it is first.');
      return;
    }
    onPut(choice);
  }

  return (
    <div className="mt-3 rounded-lg border border-navy/15 bg-parchment p-3">
      <fieldset>
        <legend className="text-sm font-medium">Whose text is it?</legend>
        <p className={`mt-1 text-xs text-navy/65 ${MEASURE}`}>
          Only somebody with the number it came from is offered. It goes on one record, once, and
          stays there.
        </p>
        <ul className="mt-2 flex flex-col">
          {row.attachTo.map((choice, index) => (
            <li key={choice.kind === 'contact' ? choice.contactId : choice.enquiryId}>
              <label className="flex min-h-11 desk:min-h-9 items-center gap-3 text-sm">
                <input
                  type="radio"
                  name={`attach-${row.id}`}
                  checked={chosen === index}
                  onChange={() => setChosen(index)}
                  className="h-5 w-5 shrink-0 accent-navy"
                />
                <span className="wrap-anywhere">{choiceWords(choice)}</span>
              </label>
            </li>
          ))}
        </ul>
      </fieldset>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button onClick={put} disabled={busy}>
          {busy ? 'Recording' : 'Put it on this record'}
        </Button>
        <Button tone="quiet" onClick={onCancel} disabled={busy}>
          Not now
        </Button>
      </div>
    </div>
  );
}

/** The kind an issue from a text starts as: a text about a clean is most often a complaint. */
const FIRST_KIND: IssueKind = 'complaint';

/**
 * AN ISSUE FROM A TEXT, on one of the household's last visits, read when the form opens and the
 * newest chosen to begin with. Found by her unless the office says not, which is the fact the
 * damage protocol turns on.
 */
function IssueFromText({
  row,
  busy,
  onRefused,
  onOpen,
  onCancel,
}: {
  row: MessageRow;
  busy: boolean;
  onRefused: (sentence: string) => void;
  onOpen: (issue: {
    visitId: string;
    kind: IssueKind;
    summary: string;
    detail: string | null;
    discoveredByClient: boolean;
  }) => void;
  onCancel: () => void;
}) {
  const [visits, setVisits] = useState<readonly MessageVisit[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [visitId, setVisitId] = useState('');
  const [kind, setKind] = useState<IssueKind>(FIRST_KIND);
  const [summary, setSummary] = useState('');
  const [detail, setDetail] = useState('');
  const [found, setFound] = useState(true);
  const field = (name: string) => `issue-${name}-${row.id}`;

  useEffect(() => {
    let live = true;
    listMessageVisits(row.id)
      .then((answer) => {
        if (!live) return;
        setVisits(answer.visits);
        setVisitId(answer.visits[0]?.id ?? '');
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setFailed(thrown instanceof ApiError ? thrown.message : 'Their visits did not load.');
      });
    return () => {
      live = false;
    };
  }, [row.id]);

  function open() {
    if (summary.trim() === '') {
      onRefused('Say what went wrong, in a sentence.');
      return;
    }
    onOpen({
      visitId,
      kind,
      summary: summary.trim(),
      detail: detail.trim() === '' ? null : detail.trim(),
      discoveredByClient: found,
    });
  }

  const labelOf = (id: string) => {
    const visit = visits?.find((one) => one.id === id);
    return visit ? `${shortDay(visit.scheduledDate)}, ${VISIT_STATUS_LABELS[visit.status]}` : id;
  };

  return (
    <div className="mt-3 rounded-lg border border-navy/15 bg-parchment p-3">
      <h4 className="mb-2 text-sm font-medium">Open an issue from this text</h4>
      {failed ? (
        <ErrorNotice title="Their visits did not load" message={failed} />
      ) : visits === null ? (
        <Busy what="their visits" />
      ) : visits.length === 0 ? (
        <>
          <p className={`text-sm ${MEASURE}`}>
            No visit at their household in the last ninety days to open an issue on.
          </p>
          <div className="mt-2">
            <Button tone="quiet" onClick={onCancel}>
              Not now
            </Button>
          </div>
        </>
      ) : (
        <>
          <SelectField
            id={field('visit')}
            label="Which visit"
            value={visitId}
            onChange={setVisitId}
            options={visits.map((visit) => visit.id)}
            toLabel={labelOf}
          />
          <SelectField
            id={field('kind')}
            label="What kind"
            value={kind}
            onChange={setKind}
            options={ISSUE_KINDS}
            toLabel={(value) => ISSUE_KIND_LABELS[value]}
          />
          <TextField
            id={field('summary')}
            label="What went wrong"
            required
            value={summary}
            onChange={setSummary}
            autoComplete="off"
          />
          <TextAreaField
            id={field('detail')}
            label="Anything more"
            value={detail}
            onChange={setDetail}
            rows={2}
            hint="The text it came from is named on the issue for you."
          />
          <CheckField
            id={field('found')}
            label="They found it before we did"
            checked={found}
            onChange={setFound}
          />
          <div className="flex flex-wrap gap-2">
            <Button onClick={open} disabled={busy}>
              {busy ? 'Recording' : 'Open the issue'}
            </Button>
            <Button tone="quiet" onClick={onCancel} disabled={busy}>
              Not now
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
